import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type { ExpandedState, Row } from '@tanstack/react-table';
import { Button } from '../../button/Button';

/**
 * テーブル行の展開/折りたたみボタン
 */
type TableExpandButtonProps<TData> = {
  row: Row<TData>;
  className?: string;
  /** 展開可能かどうかを外部から指定（省略時はrow.getCanExpand()を使用） */
  canExpand?: boolean;
  /** 展開状態 */
  isExpanded?: boolean | undefined;
  /** 展開状態変更ハンドラ（boolean版） */
  onExpandedChange?: (expanded: boolean) => void;
  /** 展開状態変更ハンドラ（ExpandedState版） */
  onExpandedStateChange?: (expanded: ExpandedState) => void;
};

export const TableExpandButton = <TData,>({
  row,
  className,
  canExpand,
  isExpanded,
  onExpandedChange,
  onExpandedStateChange,
}: TableExpandButtonProps<TData>) => {
  const isExpandable = canExpand ?? row.getCanExpand();

  if (!isExpandable) {
    return null;
  }

  const expanded = isExpanded ?? row.getIsExpanded();

  const handleClick = () => {
    if (onExpandedChange) {
      // boolean版のハンドラを使用
      onExpandedChange(!expanded);
    } else if (onExpandedStateChange) {
      // ExpandedState版のハンドラを使用
      onExpandedStateChange(expanded ? {} : { [row.id]: true });
    } else {
      // デフォルトのTanStack Tableハンドラ
      row.getToggleExpandedHandler()();
    }
  };

  return (
    <Button
      variant="non-outline"
      size="icon"
      onClick={handleClick}
      className={className}
      aria-label={expanded ? '折りたたむ' : '展開する'}
    >
      {expanded ? (
        <ChevronDownIcon className="h-4 w-4" />
      ) : (
        <ChevronRightIcon className="h-4 w-4" />
      )}
    </Button>
  );
};
